import React, { useState, useEffect, useReducer, useContext, useRef, Fragment } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ViewListIcon from "@material-ui/icons/ViewList";
import EventIcon from "@material-ui/icons/Event";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import RepeatIcon from "@material-ui/icons/Repeat";
import PersonIcon from "@material-ui/icons/Person";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AssignmentIcon from "@material-ui/icons/Assignment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TaskModal from "../../components/TaskModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import BetaBadge from "../../components/BetaBadge";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TASKS":
      return [...state, ...action.payload];
    case "UPDATE_TASKS": {
      const task = action.payload;
      const idx = state.findIndex((t) => t.id === task.id);
      if (idx !== -1) {
        state[idx] = task;
        return [...state];
      }
      return [task, ...state];
    }
    case "DELETE_TASKS":
      return state.filter((t) => t.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  /* ---- Stats ---- */
  statsRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    borderRadius: 10,
    border: `1px solid ${theme.mode === "light" ? "#e8e8e8" : "#444"}`,
    backgroundColor: theme.mode === "light" ? "#fff" : "#333",
    flex: "1 1 0",
    minWidth: 120,
  },
  statIconBox: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
  },
  statValue: {
    fontSize: "1.25rem",
    fontWeight: 700,
    lineHeight: 1,
    color: theme.mode === "light" ? "#333" : "#fff",
  },
  statLabel: {
    fontSize: "0.7rem",
    color: theme.mode === "light" ? "#999" : "#aaa",
    marginTop: 1,
  },
  /* ---- Toolbar (filters + view toggle) ---- */
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  filterSelect: {
    minWidth: 140,
    "& .MuiOutlinedInput-root": { borderRadius: 8 },
  },
  toolbarSpacer: { flex: 1 },
  viewToggle: {
    display: "flex",
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.mode === "light" ? "#ddd" : "#555"}`,
  },
  viewBtn: {
    borderRadius: 0,
    padding: "6px 10px",
    minWidth: 0,
    border: "none",
    color: theme.mode === "light" ? "#666" : "#aaa",
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },
  /* ---- Task card ---- */
  taskCard: {
    borderRadius: 10,
    border: `1px solid ${theme.mode === "light" ? "#e8e8e8" : "#444"}`,
    borderLeft: "4px solid",
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.mode === "light" ? "#fff" : "#333",
    transition: "box-shadow 0.2s",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    },
  },
  taskTitleText: {
    fontWeight: 600,
    fontSize: "0.95rem",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  completedTitle: {
    textDecoration: "line-through",
    opacity: 0.6,
  },
  priorityBadge: {
    fontSize: "0.65rem",
    height: 20,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: "0.8rem",
    color: theme.mode === "light" ? "#777" : "#aaa",
    "& .MuiSvgIcon-root": {
      fontSize: "0.9rem",
      opacity: 0.7,
    },
  },
  cardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "8px 0",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 2,
    borderTop: `1px solid ${theme.mode === "light" ? "#f0f0f0" : "#444"}`,
    paddingTop: theme.spacing(1),
    marginTop: "auto",
  },
  dueBadge: {
    fontSize: "0.65rem",
    height: 20,
    fontWeight: 600,
  },
  /* ---- Table ---- */
  tableRow: {
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#fafafa" : "#3a3a3a",
    },
  },
  /* ---- Calendar ---- */
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
    "& .calHeader": {
      textAlign: "center",
      fontWeight: 600,
      padding: 8,
      fontSize: "0.8rem",
      color: theme.mode === "light" ? "#666" : "#aaa",
    },
    "& .calDay": {
      minHeight: 80,
      padding: 4,
      border: `1px solid ${theme.mode === "light" ? "#eee" : "#444"}`,
      borderRadius: 6,
      fontSize: "0.75rem",
    },
    "& .calDayNumber": {
      fontWeight: 600,
      marginBottom: 4,
    },
    "& .calTask": {
      padding: "2px 4px",
      borderRadius: 4,
      marginBottom: 2,
      fontSize: "0.65rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: "pointer",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
  },
  emptyIcon: {
    fontSize: 48,
    color: theme.mode === "light" ? "#ccc" : "#555",
    marginBottom: theme.spacing(1),
  },
}));

const priorityColors = { low: "#22c55e", medium: "#f59e0b", high: "#ef4444" };

const Tasks = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const todayIso = new Date().toISOString().split("T")[0];

  const DAYS_PER_PAGE = 1;

  const [loading, setLoading] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [futureCursor, setFutureCursor] = useState(todayIso);
  const [hasMore, setHasMore] = useState(true);
  const [pastCursor, setPastCursor] = useState(todayIso);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [calendarTasks, setCalendarTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const loadingRef = useRef(false);
  const [searchParam, setSearchParam] = useState("");
  const [tasks, dispatch] = useReducer(reducer, []);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [viewMode, setViewMode] = useState("cards");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [orderBy, setOrderBy] = useState("dueDate");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [users, setUsers] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users", { params: { limit: -1 } });
        setUsers(data.users || []);
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TASKS", payload: data.task });
        const t = data.task;
        const isPendingOverdueOrNoDate =
          t.status === "pending" &&
          (!t.dueDate || t.dueDate < todayIso);
        setOverdueTasks((prev) => {
          const filtered = prev.filter((x) => x.id !== t.id);
          return isPendingOverdueOrNoDate ? [...filtered, t] : filtered;
        });
        setCalendarTasks((prev) => {
          const filtered = prev.filter((x) => x.id !== t.id);
          return t.dueDate ? [...filtered, t] : filtered;
        });
      }
      if (data.action === "delete") {
        const id = +data.taskId;
        dispatch({ type: "DELETE_TASKS", payload: id });
        setOverdueTasks((prev) => prev.filter((x) => x.id !== id));
        setCalendarTasks((prev) => prev.filter((x) => x.id !== id));
      }
    };
    socket.on(`company${user.companyId}-task`, handler);
    return () => socket.off(`company${user.companyId}-task`, handler);
  }, [socket, user.companyId]);

  // Carrega tasks dos proximos 30 dias a partir de hoje (1 chamada).
  useEffect(() => {
    if (viewMode === "calendar") return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const start = new Date(todayIso);
        const end = new Date(start);
        end.setDate(end.getDate() + 29);
        const dateFrom = start.toISOString().split("T")[0];
        const dateTo = end.toISOString().split("T")[0];
        const { data } = await api.get("/tasks", {
          params: {
            searchParam,
            status: statusFilter,
            priority: priorityFilter,
            assignedToUserId: assignedFilter || undefined,
            orderBy,
            limit: -1,
            dateFrom,
            dateTo,
          },
        });
        dispatch({ type: "RESET" });
        dispatch({ type: "LOAD_TASKS", payload: data.tasks || [] });
        const next = new Date(dateTo);
        next.setDate(next.getDate() + 1);
        setFutureCursor(next.toISOString().split("T")[0]);
        setHasMore(true);
        setPastCursor(todayIso);
        setHasMorePast(true);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchParam, statusFilter, priorityFilter, orderBy, assignedFilter, viewMode]);

  // Carregar tarefas vencidas e ainda nao concluidas (fixas no topo)
  useEffect(() => {
    if (viewMode === "calendar") return;
    (async () => {
      try {
        const yesterday = new Date(todayIso);
        yesterday.setDate(yesterday.getDate() - 1);
        const dateTo = yesterday.toISOString().split("T")[0];
        const [overdueRes, noDateRes] = await Promise.all([
          api.get("/tasks", {
            params: {
              searchParam,
              status: "pending",
              priority: priorityFilter,
              assignedToUserId: assignedFilter || undefined,
              orderBy: "dueDate",
              limit: -1,
              dateTo,
            },
          }),
          api.get("/tasks", {
            params: {
              searchParam,
              status: "pending",
              priority: priorityFilter,
              assignedToUserId: assignedFilter || undefined,
              orderBy: "createdAt",
              limit: -1,
              includeNoDate: "only",
            },
          }),
        ]);
        setOverdueTasks([...(overdueRes.data.tasks || []), ...(noDateRes.data.tasks || [])]);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [searchParam, priorityFilter, assignedFilter, viewMode]);

  // Carregar tarefas do mês para a visão Calendario
  useEffect(() => {
    if (viewMode !== "calendar") return;
    (async () => {
      const y = calendarMonth.getFullYear();
      const m = calendarMonth.getMonth();
      const first = new Date(y, m, 1).toISOString().split("T")[0];
      const last = new Date(y, m + 1, 0).toISOString().split("T")[0];
      try {
        const { data } = await api.get("/tasks", {
          params: {
            status: statusFilter,
            priority: priorityFilter,
            assignedToUserId: assignedFilter || undefined,
            orderBy: "dueDate",
            limit: -1,
            dateFrom: first,
            dateTo: last,
          },
        });
        setCalendarTasks(data.tasks || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [viewMode, calendarMonth, statusFilter, priorityFilter, assignedFilter]);

  const resetAndSearch = () => {
    dispatch({ type: "RESET" });
    setFutureCursor(todayIso);
    setHasMore(true);
    setPastCursor(todayIso);
    setHasMorePast(true);
  };

  const loadMore = async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const start = new Date(futureCursor);
      const end = new Date(start);
      end.setDate(end.getDate() + 30); // janela de 30 dias por chamada
      const dateFrom = start.toISOString().split("T")[0];
      const dateTo = end.toISOString().split("T")[0];
      const { data } = await api.get("/tasks", {
        params: {
          searchParam,
          status: statusFilter,
          priority: priorityFilter,
          assignedToUserId: assignedFilter || undefined,
          orderBy: "dueDate",
          limit: -1,
          dateFrom,
          dateTo,
        },
      });
      const tasksFound = data.tasks || [];
      if (tasksFound.length > 0) {
        dispatch({ type: "LOAD_TASKS", payload: tasksFound });
      } else {
        setHasMore(false);
      }
      const next = new Date(dateTo);
      next.setDate(next.getDate() + 1);
      setFutureCursor(next.toISOString().split("T")[0]);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
    loadingRef.current = false;
  };

  const loadPast = async () => {
    if (loadingPast || !hasMorePast) return;
    setLoadingPast(true);
    try {
      const end = new Date(pastCursor);
      end.setDate(end.getDate() - 1);
      const start = new Date(end);
      start.setDate(start.getDate() - 29); // janela de 30 dias para tras
      const dateFrom = start.toISOString().split("T")[0];
      const dateTo = end.toISOString().split("T")[0];
      const { data } = await api.get("/tasks", {
        params: {
          searchParam,
          status: statusFilter,
          priority: priorityFilter,
          assignedToUserId: assignedFilter || undefined,
          orderBy: "dueDate",
          limit: -1,
          dateFrom,
          dateTo,
        },
      });
      const tasksFound = data.tasks || [];
      if (tasksFound.length > 0) {
        dispatch({ type: "LOAD_TASKS", payload: tasksFound });
      } else {
        setHasMorePast(false);
      }
      setPastCursor(dateFrom);
    } catch (err) {
      toastError(err);
    }
    setLoadingPast(false);
  };

  const handleScroll = (e) => {
    if (loadingRef.current || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMore();
    }
  };

  const handleSearch = (e) => {
    setSearchParam(e.target.value.toLowerCase());
    resetAndSearch();
  };

  const handleOpenTaskModal = () => { setSelectedTask(null); setTaskModalOpen(true); };
  const handleEditTask = (task) => { setSelectedTask(task); setTaskModalOpen(true); };
  const handleCloseTaskModal = () => { setSelectedTask(null); setTaskModalOpen(false); };

  const handleDeleteTask = (task) => { setDeletingTask(task); setConfirmModalOpen(true); };
  const handleConfirmDeleteTask = async () => {
    try {
      await api.delete(`/tasks/${deletingTask.id}`);
      toast.success(i18n.t("tasks.toasts.deleted"));
    } catch (err) { toastError(err); }
    setConfirmModalOpen(false);
    setDeletingTask(null);
  };

  const handleToggleComplete = async (task) => {
    try {
      const newStatus = task.status === "completed" ? "pending" : "completed";
      await api.put(`/tasks/${task.id}`, { status: newStatus });
    } catch (err) { toastError(err); }
  };

  const filteredTasks = tasks;

  const groupedByDate = (() => {
    const map = new Map();
    filteredTasks.forEach((t) => {
      const key = t.dueDate || "__no_date__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    const dated = [...map.keys()]
      .filter((k) => k !== "__no_date__")
      .sort();
    const groups = dated.map((k) => ({ key: k, tasks: map.get(k) }));
    if (map.has("__no_date__")) {
      groups.push({ key: "__no_date__", tasks: map.get("__no_date__") });
    }
    return groups;
  })();

  // Stats
  const total = filteredTasks.length;
  const completed = filteredTasks.filter((t) => t.status === "completed").length;
  const pending = filteredTasks.filter((t) => t.status === "pending").length;
  const highCount = filteredTasks.filter((t) => t.priority === "high").length;
  const mediumCount = filteredTasks.filter((t) => t.priority === "medium").length;
  const lowCount = filteredTasks.filter((t) => t.priority === "low").length;

  const formatDate = (s) => {
    if (!s) return "-";
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("pt-BR");
  };
  const formatTime = (a, b) => {
    if (!a && !b) return null;
    return `${a || ""}${a && b ? " - " : ""}${b || ""}`;
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.status === "completed") return false;
    const today = new Date().toISOString().split("T")[0];
    return task.dueDate < today;
  };
  const isDueToday = (task) => {
    if (!task.dueDate) return false;
    return task.dueDate === new Date().toISOString().split("T")[0];
  };

  // Calendar helpers
  const getCalendarDays = () => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const days = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(y, m, d));
    return days;
  };
  const getTasksForDate = (date) => {
    if (!date) return [];
    const s = date.toISOString().split("T")[0];
    return calendarTasks.filter((t) => t.dueDate === s);
  };

  /* ============ RENDERS ============ */

  const renderStats = () => {
    const items = [
      { label: i18n.t("tasks.stats.total"), value: total, color: "#3b82f6", icon: <AssignmentIcon /> },
      { label: i18n.t("tasks.stats.completed"), value: completed, color: "#22c55e", icon: <CheckCircleIcon /> },
      { label: i18n.t("tasks.stats.pending"), value: pending, color: "#f59e0b", icon: <HourglassEmptyIcon /> },
      { label: i18n.t("tasks.stats.highPriority"), value: highCount, color: "#ef4444", icon: <span style={{ fontWeight: 800, fontSize: "1rem" }}>!</span> },
      { label: i18n.t("tasks.stats.mediumPriority"), value: mediumCount, color: "#f59e0b", icon: <span style={{ fontWeight: 800, fontSize: "1rem" }}>=</span> },
      { label: i18n.t("tasks.stats.lowPriority"), value: lowCount, color: "#22c55e", icon: <span style={{ fontWeight: 800, fontSize: "1rem" }}>-</span> },
    ];
    return (
      <div className={classes.statsRow}>
        {items.map((s, i) => (
          <div key={i} className={classes.statChip}>
            <div className={classes.statIconBox} style={{ backgroundColor: `${s.color}15`, color: s.color }}>
              {s.icon}
            </div>
            <div>
              <div className={classes.statValue}>{s.value}</div>
              <div className={classes.statLabel}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderToolbar = () => (
    <div className={classes.toolbar}>
      <FormControl variant="outlined" size="small" className={classes.filterSelect}>
        <InputLabel>{i18n.t("tasks.filters.status")}</InputLabel>
        <Select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); resetAndSearch(); }}
          label={i18n.t("tasks.filters.status")}
        >
          <MenuItem value="all">{i18n.t("tasks.filters.all")}</MenuItem>
          <MenuItem value="pending">{i18n.t("tasks.filters.pending")}</MenuItem>
          <MenuItem value="completed">{i18n.t("tasks.filters.completed")}</MenuItem>
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" className={classes.filterSelect}>
        <InputLabel>{i18n.t("tasks.filters.priority")}</InputLabel>
        <Select
          value={priorityFilter}
          onChange={(e) => { setPriorityFilter(e.target.value); resetAndSearch(); }}
          label={i18n.t("tasks.filters.priority")}
        >
          <MenuItem value="all">{i18n.t("tasks.filters.all")}</MenuItem>
          <MenuItem value="low">{i18n.t("tasks.priority.low")}</MenuItem>
          <MenuItem value="medium">{i18n.t("tasks.priority.medium")}</MenuItem>
          <MenuItem value="high">{i18n.t("tasks.priority.high")}</MenuItem>
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" className={classes.filterSelect}>
        <InputLabel>{i18n.t("tasks.filters.orderBy")}</InputLabel>
        <Select
          value={orderBy}
          onChange={(e) => { setOrderBy(e.target.value); resetAndSearch(); }}
          label={i18n.t("tasks.filters.orderBy")}
        >
          <MenuItem value="dueDate">{i18n.t("tasks.filters.dueDateAsc")}</MenuItem>
          <MenuItem value="priority">{i18n.t("tasks.filters.priorityDesc")}</MenuItem>
          <MenuItem value="createdAt">{i18n.t("tasks.filters.createdAtDesc")}</MenuItem>
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" className={classes.filterSelect}>
        <InputLabel>{i18n.t("tasks.filters.user")}</InputLabel>
        <Select
          value={assignedFilter}
          onChange={(e) => { setAssignedFilter(e.target.value); resetAndSearch(); }}
          label={i18n.t("tasks.filters.user")}
        >
          <MenuItem value="">{i18n.t("tasks.filters.all")}</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className={classes.toolbarSpacer} />

      <div className={classes.viewToggle}>
        {[
          { mode: "cards", icon: <ViewModuleIcon fontSize="small" /> },
          { mode: "list", icon: <ViewListIcon fontSize="small" /> },
          { mode: "calendar", icon: <EventIcon fontSize="small" /> },
        ].map((v) => (
          <IconButton
            key={v.mode}
            size="small"
            className={`${classes.viewBtn} ${viewMode === v.mode ? "active" : ""}`}
            onClick={() => setViewMode(v.mode)}
          >
            {v.icon}
          </IconButton>
        ))}
      </div>
    </div>
  );

  const renderTaskCard = (task) => {
    const color = priorityColors[task.priority] || "#ccc";
    const timeStr = formatTime(task.startTime, task.endTime);
    const over = isOverdue(task);
    const today = isDueToday(task);

    return (
      <Grid item xs={12} sm={6} md={4} key={task.id}>
        <div className={classes.taskCard} style={{ borderLeftColor: color }}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={task.status === "completed"}
              onChange={() => handleToggleComplete(task)}
              size="small"
              style={{ padding: 4, color }}
            />
            <Typography
              className={`${classes.taskTitleText} ${task.status === "completed" ? classes.completedTitle : ""}`}
              style={{ flex: 1 }}
            >
              {task.title}
            </Typography>
            <Chip
              label={i18n.t(`tasks.priority.${task.priority}`)}
              size="small"
              className={classes.priorityBadge}
              style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}40` }}
            />
          </Box>

          {/* Body */}
          <div className={classes.cardBody}>
            {task.description && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                {task.description.length > 80 ? task.description.substring(0, 80) + "..." : task.description}
              </Typography>
            )}

            <div className={classes.infoRow}>
              <EventIcon />
              <span>{task.dueDate ? formatDate(task.dueDate) : i18n.t("tasks.pagination.noDate")}</span>
              {task.dueDate && over && (
                <Chip label={i18n.t("tasks.card.overdue")} size="small" className={classes.dueBadge}
                  style={{ backgroundColor: "#ef444420", color: "#ef4444", marginLeft: 4 }} />
              )}
              {task.dueDate && today && !over && (
                <Chip label={i18n.t("tasks.card.dueToday")} size="small" className={classes.dueBadge}
                  style={{ backgroundColor: "#f59e0b20", color: "#f59e0b", marginLeft: 4 }} />
              )}
            </div>

            {timeStr && (
              <div className={classes.infoRow}>
                <AccessTimeIcon />
                <span>{timeStr}</span>
              </div>
            )}

            {task.recurrence && task.recurrence !== "none" && (
              <div className={classes.infoRow}>
                <RepeatIcon />
                <span>{i18n.t(`tasks.recurrence.${task.recurrence}`)}</span>
              </div>
            )}

            {task.assignedToUserIds && task.assignedToUserIds.length > 0 && (
              <div className={classes.infoRow}>
                <PersonIcon />
                <span>
                  {task.assignedToUserIds
                    .map((id) => users.find((u) => u.id === id)?.name || id)
                    .join(", ")}
                </span>
              </div>
            )}
            {(!task.assignedToUserIds || task.assignedToUserIds.length === 0) && (
              <div className={classes.infoRow}>
                <PersonIcon />
                <span>{i18n.t("tasks.filters.all")}</span>
              </div>
            )}

            {task.createdBy && (
              <div className={classes.infoRow}>
                <PersonOutlineIcon />
                <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>{task.createdBy.name}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={classes.cardFooter}>
            <Tooltip title={i18n.t("tasks.buttons.save")}>
              <IconButton size="small" onClick={() => handleEditTask(task)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t("tasks.confirmationModal.deleteTitle")}>
              <IconButton size="small" onClick={() => handleDeleteTask(task)} style={{ color: "#ef4444" }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </Grid>
    );
  };

  const renderDateHeader = (key, customLabel, color) => (
    <Grid item xs={12} key={`header-${key}`}>
      <Typography
        variant="subtitle2"
        style={{
          fontWeight: 700,
          padding: "8px 0",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          marginTop: 8,
          color: color || undefined,
        }}
      >
        {customLabel ?? (key === "__no_date__" ? i18n.t("tasks.pagination.noDate") : formatDate(key))}
      </Typography>
    </Grid>
  );

  const renderCardsView = () => (
    <Grid container spacing={2}>
      {overdueTasks.length > 0 && (
        <Fragment key="overdue">
          {renderDateHeader("overdue", `${i18n.t("tasks.pagination.overdueGroup")} (${overdueTasks.length})`, "#ef4444")}
          {overdueTasks.map(renderTaskCard)}
        </Fragment>
      )}
      {groupedByDate.map((g) => (
        <Fragment key={g.key}>
          {renderDateHeader(g.key)}
          {g.tasks.map(renderTaskCard)}
        </Fragment>
      ))}
      {filteredTasks.length === 0 && !loading && (
        <Grid item xs={12}>
          <div className={classes.emptyState}>
            <AssignmentIcon className={classes.emptyIcon} />
            <Typography color="textSecondary">{i18n.t("tasks.noTasks")}</Typography>
          </div>
        </Grid>
      )}
    </Grid>
  );

  const renderListView = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" />
          <TableCell>{i18n.t("tasks.table.title")}</TableCell>
          <TableCell>{i18n.t("tasks.table.priority")}</TableCell>
          <TableCell>{i18n.t("tasks.table.dueDate")}</TableCell>
          <TableCell>{i18n.t("tasks.table.assignedTo")}</TableCell>
          <TableCell>{i18n.t("tasks.table.status")}</TableCell>
          <TableCell align="right">{i18n.t("tasks.table.actions")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {overdueTasks.length > 0 && (
          <Fragment key="overdue">
            <TableRow>
              <TableCell colSpan={7} style={{ background: "#fef2f2", fontWeight: 700, color: "#ef4444" }}>
                {i18n.t("tasks.pagination.overdueGroup")} ({overdueTasks.length})
              </TableCell>
            </TableRow>
            {overdueTasks.map((task) => {
              const color = priorityColors[task.priority] || "#ccc";
              return (
                <TableRow key={`overdue-${task.id}`} className={classes.tableRow}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={task.status === "completed"}
                      onChange={() => handleToggleComplete(task)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography style={{ fontWeight: 500 }}>{task.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={i18n.t(`tasks.priority.${task.priority}`)}
                      size="small"
                      style={{ backgroundColor: `${color}18`, color }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(task.dueDate)}</TableCell>
                  <TableCell>
                    {task.assignedToUserIds && task.assignedToUserIds.length > 0
                      ? task.assignedToUserIds.map((id) => users.find((u) => u.id === id)?.name || id).join(", ")
                      : i18n.t("tasks.filters.all")}
                  </TableCell>
                  <TableCell>
                    <Chip label={i18n.t("tasks.card.overdue")} size="small" style={{ backgroundColor: "#ef444420", color: "#ef4444" }} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEditTask(task)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteTask(task)} style={{ color: "#ef4444" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
        {groupedByDate.map((g) => (
          <Fragment key={g.key}>
            <TableRow>
              <TableCell colSpan={7} style={{ background: "rgba(0,0,0,0.04)", fontWeight: 700 }}>
                {g.key === "__no_date__" ? i18n.t("tasks.pagination.noDate") : formatDate(g.key)}
              </TableCell>
            </TableRow>
            {g.tasks.map((task) => {
              const color = priorityColors[task.priority] || "#ccc";
              return (
                <TableRow key={task.id} className={classes.tableRow}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={task.status === "completed"}
                  onChange={() => handleToggleComplete(task)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography style={{
                  fontWeight: 500,
                  textDecoration: task.status === "completed" ? "line-through" : "none",
                  opacity: task.status === "completed" ? 0.6 : 1,
                }}>
                  {task.title}
                </Typography>
                {task.description && (
                  <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                    {task.description.substring(0, 60)}{task.description.length > 60 ? "..." : ""}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={i18n.t(`tasks.priority.${task.priority}`)}
                  size="small"
                  className={classes.priorityBadge}
                  style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}40` }}
                />
              </TableCell>
              <TableCell>
                <span>{formatDate(task.dueDate)}</span>
                {isOverdue(task) && (
                  <Chip label={i18n.t("tasks.card.overdue")} size="small" className={classes.dueBadge}
                    style={{ backgroundColor: "#ef444420", color: "#ef4444", marginLeft: 6 }} />
                )}
              </TableCell>
              <TableCell>
                {task.assignedToUserIds && task.assignedToUserIds.length > 0
                  ? task.assignedToUserIds.map((id) => users.find((u) => u.id === id)?.name || id).join(", ")
                  : i18n.t("tasks.filters.all")
                }
              </TableCell>
              <TableCell>
                <Chip
                  label={task.status === "completed" ? i18n.t("tasks.status.completed") : i18n.t("tasks.status.pending")}
                  size="small"
                  style={{
                    backgroundColor: task.status === "completed" ? "#22c55e18" : "#f59e0b18",
                    color: task.status === "completed" ? "#22c55e" : "#f59e0b",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleEditTask(task)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteTask(task)} style={{ color: "#ef4444" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
              );
            })}
          </Fragment>
        ))}
        {filteredTasks.length === 0 && !loading && (
          <TableRow>
            <TableCell colSpan={7} align="center">
              <div className={classes.emptyState}>
                <AssignmentIcon className={classes.emptyIcon} />
                <Typography color="textSecondary">{i18n.t("tasks.noTasks")}</Typography>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const renderCalendarView = () => {
    const days = getCalendarDays();
    const weekDays = ["weekDays.sun", "weekDays.mon", "weekDays.tue", "weekDays.wed", "weekDays.thu", "weekDays.fri", "weekDays.sat"]
      .map((k) => i18n.t(`tasks.calendar.${k}`));
    const monthNames = ["months.jan", "months.feb", "months.mar", "months.apr", "months.may", "months.jun",
      "months.jul", "months.aug", "months.sep", "months.oct", "months.nov", "months.dec"]
      .map((k) => i18n.t(`tasks.calendar.${k}`));

    return (
      <div>
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={2}>
          <IconButton size="small"
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" style={{ fontWeight: 600, minWidth: 200, textAlign: "center" }}>
            {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
          </Typography>
          <IconButton size="small"
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <div className={classes.calendarGrid}>
          {weekDays.map((d) => <div key={d} className="calHeader">{d}</div>)}
          {days.map((date, idx) => {
            const dayTasks = date ? getTasksForDate(date) : [];
            const isToday = date && date.toISOString().split("T")[0] === new Date().toISOString().split("T")[0];
            return (
              <div key={idx} className="calDay"
                style={{
                  backgroundColor: isToday ? "rgba(59,130,246,0.06)" : date ? "transparent" : "rgba(0,0,0,0.02)",
                  border: isToday ? "2px solid #3b82f6" : undefined,
                }}
              >
                {date && (
                  <>
                    <div className="calDayNumber" style={{ color: isToday ? "#3b82f6" : undefined }}>
                      {date.getDate()}
                    </div>
                    {dayTasks.slice(0, 3).map((t) => (
                      <div key={t.id} className="calTask"
                        style={{
                          backgroundColor: `${priorityColors[t.priority] || "#ccc"}25`,
                          color: priorityColors[t.priority] || "#666",
                        }}
                        onClick={() => handleEditTask(t)}
                      >
                        {t.title}
                      </div>
                    ))}
                    {dayTasks.length > 3 && (
                      <div style={{ fontSize: "0.6rem", color: "#999", textAlign: "center" }}>+{dayTasks.length - 3}</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Title>{i18n.t("tasks.title")}</Title>
          <BetaBadge />
        </div>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("tasks.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTaskModal}
            startIcon={<AddIcon />}
          >
            {i18n.t("tasks.buttons.newTask")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {renderStats()}
        {renderToolbar()}

        {viewMode !== "calendar" && hasMorePast && (
          <Box display="flex" justifyContent="center" my={2}>
            <Button onClick={loadPast} disabled={loadingPast} variant="outlined" size="small">
              {loadingPast ? i18n.t("tasks.pagination.loading") : i18n.t("tasks.pagination.loadPrevious")}
            </Button>
          </Box>
        )}

        {viewMode === "cards" && renderCardsView()}
        {viewMode === "list" && renderListView()}
        {viewMode === "calendar" && renderCalendarView()}

        {viewMode !== "calendar" && hasMore && (
          <Box display="flex" justifyContent="center" my={2}>
            <Button onClick={loadMore} disabled={loading} variant="outlined" size="small">
              {loading ? i18n.t("tasks.pagination.loading") : i18n.t("tasks.pagination.loadMore")}
            </Button>
          </Box>
        )}
      </Paper>

      <TaskModal
        open={taskModalOpen}
        onClose={handleCloseTaskModal}
        task={selectedTask}
        users={users}
      />

      <ConfirmationModal
        title={i18n.t("tasks.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmDeleteTask}
      >
        {i18n.t("tasks.confirmationModal.deleteMessage")}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Tasks;
